/**
 * Management Dashboard (Oct 2026) — Founder/Admin-only month-wise studio
 * overview requested by the client. Fed by GET /finance/management-dashboard.
 * Revenue / Direct cost / Margin / Project profitability are scoped to the
 * selected month; Cash Position and Receivables Ageing are the current live
 * snapshot. All amounts INR.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmt } from "@/lib/finance";
import { StatCard } from "@/components/Layout";

const pct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

export default function ManagementDashboard() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api.get("/finance/management-dashboard", { params: { month } })
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.status === 403 ? "This dashboard is available to Founders and Admins only." : "Couldn't load the dashboard — please try again."));
  }, [month]);

  const kf = data?.key_figures || {};

  return (
    <div data-testid="management-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-2 mb-4">
        <div>
          <div className="font-display text-lg font-bold text-slate-900">Management Dashboard</div>
          <div className="text-xs text-slate-500">Analogy Design — month-wise studio overview</div>
        </div>
        <div className="flex items-center gap-2">
          <label className="label-overline">Month</label>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm border border-slate-300 h-8 text-xs px-2" data-testid="md-month" />
        </div>
      </div>

      {err && <div className="text-xs text-slate-400 p-4" data-testid="md-error">{err}</div>}
      {!data && !err && <div className="text-xs text-slate-400 p-4">Loading…</div>}

      {data && (
        <div className="space-y-5">
          {/* Key figures */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="md-key-figures">
            <StatCard label="Cash" value={fmt(kf.cash)} />
            <StatCard label="Receivables" value={fmt(kf.receivables)} />
            <StatCard label="Revenue" value={fmt(kf.revenue)} />
            <StatCard label="Direct cost" value={fmt(kf.direct_cost)} accent="text-orange-700" />
            <StatCard label="Margin %" value={pct(kf.margin_pct)} />
            <StatCard label="Billable-only margin %" value={pct(kf.billable_only_margin_pct)} />
            <StatCard label="Headcount" value={kf.headcount ?? "—"} />
            <StatCard label="Gross salary" value={fmt(kf.gross_salary)} />
            <StatCard label="Salary % of revenue" value={pct(kf.salary_pct_rev)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* 1. Cash position */}
            <div>
              <div className="label-overline mb-1">1. Cash position (live)</div>
              <div className="card-flat overflow-x-auto">
                <table className="w-full text-xs" data-testid="md-cash">
                  <thead className="bg-slate-50"><tr>
                    <th className="label-overline px-2 py-2 text-left">Account</th>
                    <th className="label-overline px-2 py-2 text-right">Closing</th>
                    <th className="label-overline px-2 py-2 text-right">% of total</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.cash_position.map((c) => (
                      <tr key={c.name}><td className="px-2 py-1.5">{c.name}</td><td className="px-2 py-1.5 text-right font-mono">{fmt(c.closing)}</td><td className="px-2 py-1.5 text-right">{c.pct}%</td></tr>
                    ))}
                    {data.cash_position.length === 0 && <tr><td colSpan={3} className="px-2 py-4 text-center text-slate-400">No bank accounts configured.</td></tr>}
                  </tbody>
                  <tfoot><tr className="border-t-2 border-slate-200 font-semibold"><td className="px-2 py-1.5">Total cash</td><td className="px-2 py-1.5 text-right font-mono">{fmt(data.total_cash)}</td><td /></tr></tfoot>
                </table>
              </div>
            </div>

            {/* 2. Receivables ageing */}
            <div>
              <div className="label-overline mb-1">2. Receivables ageing (live)</div>
              <div className="card-flat overflow-x-auto">
                <table className="w-full text-xs" data-testid="md-ageing">
                  <thead className="bg-slate-50"><tr>
                    <th className="label-overline px-2 py-2 text-left">Bucket</th>
                    <th className="label-overline px-2 py-2 text-right">Amount</th>
                    <th className="label-overline px-2 py-2 text-right">% of total</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.receivables_ageing.map((b) => (
                      <tr key={b.bucket}><td className="px-2 py-1.5">{b.bucket} days</td><td className="px-2 py-1.5 text-right font-mono">{fmt(b.amount)}</td><td className="px-2 py-1.5 text-right">{b.pct}%</td></tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="border-t-2 border-slate-200 font-semibold"><td className="px-2 py-1.5">Total</td><td className="px-2 py-1.5 text-right font-mono">{fmt(data.receivables_total)}</td><td /></tr></tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* 3. Project profitability */}
          <div>
            <div className="label-overline mb-1">3. Project profitability — {data.month}</div>
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-xs" data-testid="md-projects">
                <thead className="bg-slate-50"><tr>
                  <th className="label-overline px-2 py-2 text-left">Project</th>
                  <th className="label-overline px-2 py-2 text-right">Revenue</th>
                  <th className="label-overline px-2 py-2 text-right">Direct cost</th>
                  <th className="label-overline px-2 py-2 text-right">Margin</th>
                  <th className="label-overline px-2 py-2 text-right">Margin %</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.project_profitability.length === 0 && <tr><td colSpan={5} className="px-2 py-6 text-center text-slate-400">No revenue or costs recorded for {data.month}.</td></tr>}
                  {data.project_profitability.map((p) => (
                    <tr key={p.project}>
                      <td className="px-2 py-1.5">{p.project}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmt(p.revenue)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-orange-700">{fmt(p.direct_cost)}</td>
                      <td className={`px-2 py-1.5 text-right font-mono ${p.margin >= 0 ? "text-green-700" : "text-red-700"}`}>{fmt(p.margin)}</td>
                      <td className="px-2 py-1.5 text-right">{pct(p.margin_pct)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 border-slate-200 font-semibold">
                  <td className="px-2 py-1.5">Total</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(data.project_totals.revenue)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(data.project_totals.direct_cost)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(data.project_totals.margin)}</td>
                  <td className="px-2 py-1.5 text-right">{pct(data.project_totals.margin_pct)}</td>
                </tr></tfoot>
              </table>
            </div>
          </div>

          {/* 4. Headcount cost */}
          <div>
            <div className="label-overline mb-1">4. Headcount cost (current roster)</div>
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-xs" data-testid="md-headcount">
                <thead className="bg-slate-50"><tr>
                  <th className="label-overline px-2 py-2 text-left">Name</th>
                  <th className="label-overline px-2 py-2 text-right">Gross salary</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {data.headcount_cost.map((h, i) => (
                    <tr key={`${h.name}-${i}`}><td className="px-2 py-1.5">{h.name}</td><td className="px-2 py-1.5 text-right font-mono">{fmt(h.gross_salary)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr className="border-t-2 border-slate-200 font-semibold"><td className="px-2 py-1.5">Total ({kf.headcount})</td><td className="px-2 py-1.5 text-right font-mono">{fmt(kf.gross_salary)}</td></tr></tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
